import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const PartFormModal = ({ part, moduleId, moduleProjectId, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    partCode: '',
    partName: '',
    description: '',
    materialId: null,
    materialName: '',
    materialCategory: '',
    color: '',
    width: '',
    height: '',
    thickness: '',
    quantity: 1,
    edgeBandTop: 0,
    edgeBandBottom: 0,
    edgeBandLeft: 0,
    edgeBandRight: 0,
    edgeBandType: '',
    orientation: 'A',
    mirroredAllowed: false,
    grainDirection: '',
    productionStatus: 'PENDING'
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (part) {
      setFormData({
        partCode: part.part_code || '',
        partName: part.part_name || '',
        description: part.description || '',
        materialId: part.material_id || null,
        materialName: part.material_name || '',
        materialCategory: part.material_category || '',
        color: part.color || '',
        width: part.width || '',
        height: part.height || '',
        thickness: part.thickness || '',
        quantity: part.quantity || 1,
        edgeBandTop: part.edge_band_top || 0,
        edgeBandBottom: part.edge_band_bottom || 0,
        edgeBandLeft: part.edge_band_left || 0,
        edgeBandRight: part.edge_band_right || 0,
        edgeBandType: part.edge_band_type || '',
        orientation: part.orientation || 'A',
        mirroredAllowed: part.mirrored_allowed || false,
        grainDirection: part.grain_direction || '',
        productionStatus: part.production_status || 'PENDING'
      });
    } else {
      setFormData({
        partCode: '',
        partName: '',
        description: '',
        materialId: null,
        materialName: '',
        materialCategory: '',
        color: '',
        width: '',
        height: '',
        thickness: '',
        quantity: 1,
        edgeBandTop: 0,
        edgeBandBottom: 0,
        edgeBandLeft: 0,
        edgeBandRight: 0,
        edgeBandType: '',
        orientation: 'A',
        mirroredAllowed: false,
        grainDirection: '',
        productionStatus: 'PENDING'
      });
    }
  }, [part, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        partCode: formData.partCode,
        partName: formData.partName,
        description: formData.description || null,
        materialId: formData.materialId,
        materialName: formData.materialName,
        materialCategory: formData.materialCategory || null,
        color: formData.color || null,
        width: parseFloat(formData.width),
        height: parseFloat(formData.height),
        thickness: parseFloat(formData.thickness),
        quantity: parseInt(formData.quantity) || 1,
        edgeBandTop: parseInt(formData.edgeBandTop) || 0,
        edgeBandBottom: parseInt(formData.edgeBandBottom) || 0,
        edgeBandLeft: parseInt(formData.edgeBandLeft) || 0,
        edgeBandRight: parseInt(formData.edgeBandRight) || 0,
        edgeBandType: formData.edgeBandType || null,
        orientation: formData.orientation,
        mirroredAllowed: formData.mirroredAllowed,
        grainDirection: formData.grainDirection || null,
        productionStatus: formData.productionStatus,
        projectId: moduleProjectId
      };

      await onSave(part?.id, data);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar peça:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {part ? 'Editar Peça' : 'Nova Peça'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código da Peça *
              </label>
              <input
                type="text"
                name="partCode"
                value={formData.partCode}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="PRT-001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status de Produção
              </label>
              <select
                name="productionStatus"
                value={formData.productionStatus}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="PENDING">Pendente</option>
                <option value="IN_PROGRESS">Em Andamento</option>
                <option value="COMPLETED">Concluído</option>
                <option value="CANCELLED">Cancelado</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome da Peça *
            </label>
            <input
              type="text"
              name="partName"
              value={formData.partName}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Tampo Superior"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Largura (mm) *
              </label>
              <input
                type="number"
                name="width"
                value={formData.width}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Altura (mm) *
              </label>
              <input
                type="number"
                name="height"
                value={formData.height}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Espessura (mm) *
              </label>
              <input
                type="number"
                name="thickness"
                value={formData.thickness}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="18"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Material *
              </label>
              <input
                type="text"
                name="materialName"
                value={formData.materialName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="MDF"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantidade *
              </label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                required
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria do Material
              </label>
              <input
                type="text"
                name="materialCategory"
                value={formData.materialCategory}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="MDF Branco"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cor
              </label>
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Branco"
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fita Topo (mm)
              </label>
              <input
                type="number"
                name="edgeBandTop"
                value={formData.edgeBandTop}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fita Base (mm)
              </label>
              <input
                type="number"
                name="edgeBandBottom"
                value={formData.edgeBandBottom}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fita Esq. (mm)
              </label>
              <input
                type="number"
                name="edgeBandLeft"
                value={formData.edgeBandLeft}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fita Dir. (mm)
              </label>
              <input
                type="number"
                name="edgeBandRight"
                value={formData.edgeBandRight}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Orientação
              </label>
              <select
                name="orientation"
                value={formData.orientation}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="A">A (Automática)</option>
                <option value="H">H (Horizontal)</option>
                <option value="V">V (Vertical)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Direção do Grão
              </label>
              <input
                type="text"
                name="grainDirection"
                value={formData.grainDirection}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="H ou V"
              />
            </div>
            <div className="flex items-center pt-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="mirroredAllowed"
                  checked={formData.mirroredAllowed}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Espelhamento Permitido</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartFormModal;
